import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { checkRequest, hoursBetween } from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { date, money, plural, WORDS } from '../../lib/format';
import { RequestFacts, type RequestRow, type RequestTypeRow } from '../hr/Requests';

/**
 * طلباتي.
 *
 * الأذوناتُ والمصروفاتُ والخطابات — وإجازاتي في شاشتها لأنّها وحدَها ما
 * يخصم رصيدًا. والقواعدُ تُقرأ في النموذج قبل الإرسال: من يُردّ ثلاثَ
 * مرّاتٍ لثلاثة أسبابٍ متتالية يظنّ النظام يعانده.
 */
export function MyRequests() {
  const [writing, setWriting] = useState(false);

  const list = useQuery({
    queryKey: ['hr', 'me', 'requests'],
    queryFn: () => api<RequestRow[]>('/hr/me/requests'),
    retry: false,
  });

  const rows = list.data ?? [];
  const open = rows.filter((r) => r.open);

  if (list.isError) {
    return (
      <div className="page">
        <PageHead title="طلباتي" />
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHead
        title="طلباتي"
        subtitle="الأذونات والمصروفات والخطابات"
        actions={
          <button className="btn btn-primary" onClick={() => setWriting(true)}>
            <Icon name="plus" size={15} />
            طلب جديد
          </button>
        }
      />

      <Notice title="الإجازة في شاشتها" icon="calendar">
        الإجازةُ تخصم من رصيدك وتدخل حسابَ مرتّبك، فلها شاشتُها في «
        <a className="link" href="/my/leaves">إجازاتي</a>».
      </Notice>

      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا طلبات بعد"
          text="اطلب إذنًا أو ردَّ مصروفٍ أو خطابَ تعريف — والقواعدُ تُقرأ في النموذج."
          action={
            <button className="btn btn-primary" onClick={() => setWriting(true)}>
              اكتب طلبًا
            </button>
          }
        />
      )}

      {rows.length > 0 && (
        <div className="card">
          <p className="tiny mute">
            {plural(open.length, WORDS.record)} قيد النظر من{' '}
            {plural(rows.length, WORDS.record)}.
          </p>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>الطلب</th>
                  <th>التفاصيل</th>
                  <th>قُدّم</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className="cell-strong">{r.typeLabel}</span>
                      <div className="tiny mute">{r.kindLabel}</div>
                    </td>
                    <td className="cell-mute">
                      <RequestFacts r={r} />
                    </td>
                    <td className="num cell-mute">{date(r.createdAt)}</td>
                    <td>
                      <Badge kind={r.statusKind}>{r.statusLabel}</Badge>
                      {r.kind === 'EXPENSE' && r.status === 'APPROVED' && (
                        <div className="tiny mute">
                          {r.reimbursed
                            ? 'رُدّ في المرتّب'
                            : 'ينتظر الردّ في المرتّب'}
                        </div>
                      )}
                    </td>
                    <td>
                      {r.status === 'PENDING' && <Withdraw id={r.id} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {writing && <RequestDialog onClose={() => setWriting(false)} />}
    </div>
  );
}

function Withdraw({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const pull = useMutation({
    mutationFn: () =>
      api(`/hr/me/requests/${id}/withdraw`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast('سُحب طلبك');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر السحب'),
  });

  return (
    <button
      className="btn btn-sm"
      onClick={() => pull.mutate()}
      disabled={pull.isPending}
    >
      اسحب
    </button>
  );
}

/**
 * نموذجُ الطلب.
 *
 * حقولُه تتبع قواعدَ النوع المختار: من يطلب خطابًا لا يُسأل عن ساعات،
 * ومن يطلب إذنًا لا يُسأل عن مبلغ. والقواعدُ تُفحص هنا بالدالّة نفسِها
 * التي يفحص بها الخادم — فيرى سببَ المنع قبل الإرسال لا بعده.
 */
function RequestDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const types = useQuery({
    queryKey: ['hr', 'me', 'request-types'],
    queryFn: () => api<RequestTypeRow[]>('/hr/me/request-types'),
  });

  const [typeId, setTypeId] = useState('');
  const [onDate, setOnDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const type = types.data?.find((t) => t.id === typeId) ?? null;

  /* الساعاتُ تُحسب من الوقتين ولا تُكتب: رقمان يُكتبان يختلفان. */
  const hours =
    fromTime && toTime ? hoursBetween(fromTime, toTime) : null;

  const draft = {
    onDate: onDate || null,
    toDate: toDate || null,
    fromTime: fromTime || null,
    toTime: toTime || null,
    hours,
    amount: amount ? Number(amount) : null,
    reason: reason || null,
    /* المرفقُ لم يُبنَ رفعُه في هذه الشاشة بعد — ويُقال ذلك صراحةً. */
    attachmentId: null,
  };

  const errors = type
    ? checkRequest(
        {
          needsDates: type.needsDates,
          needsHours: type.needsHours,
          needsAmount: type.needsAmount,
          needsReason: type.needsReason,
          /* لا يُفحص هنا: الرفعُ غيرُ متاحٍ بعد فلا يُمنع الطلب به. */
          needsAttachment: false,
          maxPerMonth: null,
          maxHours: type.maxHours,
          minNoticeDays: type.minNoticeDays,
        },
        draft,
        { usedThisMonth: 0, today: new Date().toISOString().slice(0, 10) },
      )
    : ['اختر نوع الطلب.'];

  const send = useMutation({
    mutationFn: () =>
      api('/hr/me/requests', {
        method: 'POST',
        body: { typeId, ...draft },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast('أُرسل طلبك');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإرسال'),
  });

  return (
    <Modal
      title="طلب جديد"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => send.mutate()}
            disabled={errors.length > 0 || send.isPending}
          >
            {send.isPending ? 'جارٍ الإرسال' : 'أرسل'}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="label req" htmlFor="rq-type">
          نوع الطلب
        </label>
        <select
          id="rq-type"
          className="select"
          value={typeId}
          onChange={(e) => setTypeId(e.target.value)}
        >
          <option value="">اختر</option>
          {(types.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {type && (
        <>
          {type.description && <p className="tiny mute">{type.description}</p>}

          {/* القواعدُ تُقرأ قبل الملء لا بعد الردّ. */}
          <div className="rc-offer-facts">
            {type.maxPerMonth !== null && (
              <span>حتى {type.maxPerMonth} في الشهر</span>
            )}
            {type.maxHours !== null && (
              <span>حتى {type.maxHours} ساعات للطلب</span>
            )}
            {type.minNoticeDays !== null && (
              <span>قبله بـ{type.minNoticeDays} يوم على الأقلّ</span>
            )}
            {type.needsAttachment && <span>يحتاج مرفقًا</span>}
            <span>
              يبتّ فيه {type.approverRole === 'ADMIN' ? 'مدير النظام' : 'مديرك'}
            </span>
          </div>

          <div className="form-grid mt-4">
            {type.needsDates && (
              <>
                <div className="field">
                  <label className="label req" htmlFor="rq-date">
                    التاريخ
                  </label>
                  <input
                    id="rq-date"
                    className="input num"
                    type="date"
                    value={onDate}
                    onChange={(e) => setOnDate(e.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="label" htmlFor="rq-to">
                    حتى
                  </label>
                  <input
                    id="rq-to"
                    className="input num"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                  <span className="tiny mute">ليومٍ واحدٍ اتركه فارغًا.</span>
                </div>
              </>
            )}

            {type.needsHours && (
              <>
                <div className="field">
                  <label className="label req" htmlFor="rq-from-time">
                    من الساعة
                  </label>
                  <input
                    id="rq-from-time"
                    className="input num"
                    type="time"
                    value={fromTime}
                    onChange={(e) => setFromTime(e.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="label req" htmlFor="rq-to-time">
                    إلى الساعة
                  </label>
                  <input
                    id="rq-to-time"
                    className="input num"
                    type="time"
                    value={toTime}
                    onChange={(e) => setToTime(e.target.value)}
                  />
                  {hours !== null && hours > 0 && (
                    <span className="tiny mute">{hours} ساعة.</span>
                  )}
                </div>
              </>
            )}

            {type.needsAmount && (
              <div className="field">
                <label className="label req" htmlFor="rq-amount">
                  المبلغ
                </label>
                <input
                  id="rq-amount"
                  className="input num"
                  type="number"
                  min={0}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            )}

            <div className="field span-2">
              <label
                className={`label${type.needsReason ? ' req' : ''}`}
                htmlFor="rq-reason"
              >
                السبب
              </label>
              <textarea
                id="rq-reason"
                className="input"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </div>

          {type.needsAttachment && (
            <Notice title="المرفق يُسلَّم يدًا" icon="paperclip" kind="warning">
              هذا النوع يشترط مرفقًا، ورفعُ الملفّات من هذه الشاشة لم يُبنَ
              بعد — سلّم الإيصال إلى الموارد البشرية، وسيُردّ طلبُك من
              الخادم حتى يُرفق.
            </Notice>
          )}

          {errors.length > 0 && typeId && (
            <ul className="rc-qlist mt-2">
              {errors.map((e) => (
                <li key={e} className="tiny" style={{ color: 'var(--danger)' }}>
                  {e}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Modal>
  );
}
